import { InputGroup, InputRightAddon } from "@chakra-ui/react";
import { ComponentType, ForwardedRef, forwardRef, ReactNode } from "react";
import { FieldValues, Path } from "react-hook-form";
import { Writeable } from "utils/typescriptUtilis";
import { DimensionPatch } from "__generated__/gql/graphql";
import Fieldset, { Legend } from "components/Shared/Forms/Fieldset";
import type { ControlledFormFieldProps } from "./FormField";
import NumberInput, { Props as NumberInputProps } from "./NumberInput";
import InlineFieldsWrapper, {
  InlineFieldsWrapperProps,
} from "./InlineFieldsWrapper";

function InchesInputInner(
  props: NumberInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <InputGroup>
      <NumberInput
        ref={ref}
        thousandSeparator
        decimalScale={4}
        max={999.9999}
        allowNegative={false}
        borderRightRadius={0}
        textAlign="right"
        {...props}
      />
      <InputRightAddon px=".5em">in</InputRightAddon>
    </InputGroup>
  );
}

const InchesInput = forwardRef(InchesInputInner);

export const DIMENSIONS_FIELDS = ["height", "width", "depth"] as const;

export type DimensionsFieldsetType = Writeable<
  Pick<DimensionPatch, typeof DIMENSIONS_FIELDS[number]>
>;

type DimensionsFields = Path<DimensionsFieldsetType>;

export type AllowableFieldValues<
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues> = Path<TFieldValues>,
> = TFieldName extends `${infer TPrefix}.${DimensionsFields}` ? TPrefix : never;

type Props<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  >,
> = InlineFieldsWrapperProps & {
  ControlledFormFieldComponent: ComponentType<
    ControlledFormFieldProps<TFieldValues>
  >;
  fieldNamePrefix: TFieldNamePrefix;
  isDisabled?: boolean;
  legend?: ReactNode;
  legendLabel?: ReactNode;
};

export default function DimensionsFieldset<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  > = Extract<Path<TFieldValues>, AllowableFieldValues<TFieldValues>>,
>({
  ControlledFormFieldComponent,
  fieldNamePrefix,
  isDisabled,
  legend,
  legendLabel,
  ...rest
}: Props<TFieldValues, TFieldNamePrefix>) {
  function getPrefixedFieldName(name: DimensionsFields) {
    return `${fieldNamePrefix}.${name}` as Path<TFieldValues>;
  }

  return (
    <InlineFieldsWrapper
      as={Fieldset}
      fieldWrapWidth="5em"
      {...rest}
      sx={{
        "> .chakra-form-control > label": { fontSize: "sm", mb: ".25em" },
        ...rest.sx,
      }}
    >
      {legend ||
        (legendLabel && (
          <Legend color="magenta.veryDark" fontSize="md" mb={1}>
            {legendLabel}
          </Legend>
        ))}

      <ControlledFormFieldComponent
        name={getPrefixedFieldName("height")}
        isDisabled={isDisabled}
        renderInput={({ field }) => <InchesInput {...field} />}
      />

      <ControlledFormFieldComponent
        name={getPrefixedFieldName("width")}
        isDisabled={isDisabled}
        renderInput={({ field }) => <InchesInput {...field} />}
      />

      <ControlledFormFieldComponent
        name={getPrefixedFieldName("depth")}
        isDisabled={isDisabled}
        renderInput={({ field }) => <InchesInput {...field} />}
      />
    </InlineFieldsWrapper>
  );
}
